import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  BarChart3, Clock, Euro, Building2, Loader2, Map as MapIcon, ArrowLeft,
} from "lucide-react";

// Iter123k203 · Dashboard Statistiche CoPilot.
// Collegata a GET /api/copilot/statistiche. 4 KPI + barre per condominio + grafico mensile.

const fmtMin = (m) => {
  if (!m) return "0m";
  const h = Math.floor(m / 60); const mm = m % 60;
  return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
};

const fmtEuro = (c) => `€ ${(c || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MESE_LABEL = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
const labelMese = (yyyyMM) => {
  const [y, m] = (yyyyMM || "").split("-");
  const idx = parseInt(m || "0", 10) - 1;
  if (idx < 0 || idx > 11) return yyyyMM;
  return `${MESE_LABEL[idx]} ${y.slice(2)}`;
};

const colorForDurata = (min) => {
  if (!min) return "bg-slate-300";
  if (min < 30) return "bg-emerald-500";
  if (min <= 90) return "bg-yellow-500";
  return "bg-red-500";
};

export default function CoPilotStatistiche() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/copilot/statistiche")
      .then((r) => setStats(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento"))
      .finally(() => setLoading(false));
  }, []);

  const maxDurataCond = useMemo(() => {
    if (!stats?.per_condominio?.length) return 1;
    return Math.max(...stats.per_condominio.map((c) => c.durata_min)) || 1;
  }, [stats]);

  const maxMese = useMemo(() => {
    if (!stats?.serie_mensile?.length) return 1;
    return Math.max(...stats.serie_mensile.map((m) => m.durata_min)) || 1;
  }, [stats]);

  if (loading) {
    return (
      <div className="text-center py-16 text-slate-500" data-testid="copilot-stats-loading">
        <Loader2 className="inline w-6 h-6 animate-spin mr-2" /> Caricamento statistiche…
      </div>
    );
  }
  if (!stats) return null;

  const kpi = stats.totali;
  const vuoto = kpi.sopralluoghi === 0;

  return (
    <div className="space-y-5" data-testid="copilot-stats-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button data-testid="copilot-stats-back" onClick={() => navigate("/copilot/mappa")}
            className="p-2 rounded-full hover:bg-slate-100">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-bold text-brand flex items-center gap-2">
            <BarChart3 className="w-7 h-7" /> Statistiche CoPilot
          </h1>
        </div>
        <Button data-testid="copilot-stats-mappa" onClick={() => navigate("/copilot/mappa")}
          className="bg-slate-700 hover:bg-slate-800 text-white">
          <MapIcon className="w-4 h-4 mr-1" /> Vai alla mappa
        </Button>
      </div>

      {vuoto && (
        <Card className="p-6 text-center text-slate-500" data-testid="copilot-stats-empty">
          <BarChart3 className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          Nessun sopralluogo concluso ancora. Chiudi un sopralluogo per vedere i dati qui.
        </Card>
      )}

      {/* 4 KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4" data-testid="kpi-sopralluoghi">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-bold uppercase mb-1">
            <BarChart3 className="w-4 h-4" /> Sopralluoghi
          </div>
          <div className="text-3xl font-bold text-slate-800">{kpi.sopralluoghi}</div>
        </Card>
        <Card className="p-4" data-testid="kpi-durata">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-bold uppercase mb-1">
            <Clock className="w-4 h-4" /> Tempo totale
          </div>
          <div className="text-3xl font-bold text-slate-800">{fmtMin(kpi.durata_min)}</div>
        </Card>
        <Card className="p-4" data-testid="kpi-costo">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-bold uppercase mb-1">
            <Euro className="w-4 h-4" /> Costo totale
          </div>
          <div className="text-3xl font-bold text-brand">{fmtEuro(kpi.costo_totale)}</div>
        </Card>
        <Card className="p-4" data-testid="kpi-condomini">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-bold uppercase mb-1">
            <Building2 className="w-4 h-4" /> Condomini visitati
          </div>
          <div className="text-3xl font-bold text-slate-800">{kpi.condomini}</div>
        </Card>
      </div>

      {/* Grafico mensile */}
      <Card className="p-4" data-testid="chart-mensile">
        <div className="flex items-center justify-between mb-3">
          <div className="font-bold text-slate-700">Tempo per mese · ultimi 12 mesi</div>
          <div className="text-xs text-slate-500">Max {fmtMin(maxMese)}</div>
        </div>
        <div className="flex items-end gap-1 h-40 pt-2">
          {stats.serie_mensile.map((m) => {
            const pct = Math.round((m.durata_min / maxMese) * 100);
            const h = m.durata_min > 0 ? Math.max(pct, 4) : 0;
            return (
              <div key={m.mese} data-testid={`bar-${m.mese}`}
                className="flex-1 flex flex-col items-center justify-end group relative">
                {m.durata_min > 0 && (
                  <div className="absolute -top-7 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10
                    bg-slate-900 text-white text-[10px] rounded px-1.5 py-0.5 whitespace-nowrap">
                    {fmtMin(m.durata_min)} · {fmtEuro(m.costo_totale)}
                  </div>
                )}
                <div className={`w-full rounded-t transition-all ${colorForDurata(m.durata_min)}`}
                  style={{ height: `${h}%`, minHeight: m.durata_min > 0 ? 6 : 0 }} />
                <div className="text-[10px] text-slate-500 mt-1 truncate w-full text-center">
                  {labelMese(m.mese)}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-3 text-[10px] text-slate-500 mt-3">
          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-emerald-500" />&lt;30m/mese</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-yellow-500" />30-90m</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-red-500" />&gt;90m</span>
        </div>
      </Card>

      {/* Barre per condominio */}
      <Card className="p-4" data-testid="chart-condomini">
        <div className="font-bold text-slate-700 mb-3">Dove hai investito più tempo</div>
        {stats.per_condominio.length === 0 ? (
          <div className="text-sm text-slate-400 text-center py-4">Nessun dato ancora.</div>
        ) : (
          <div className="space-y-2.5">
            {stats.per_condominio.map((c) => {
              const pct = Math.round((c.durata_min / maxDurataCond) * 100);
              return (
                <div key={c.condominio_id} data-testid={`bar-cond-${c.condominio_id}`}>
                  <div className="flex items-center justify-between mb-1 gap-2 text-sm">
                    <div className="font-semibold truncate flex-1">{c.condominio_nome}</div>
                    <div className="text-xs text-slate-500 whitespace-nowrap flex items-center gap-2">
                      <span className="font-bold">{fmtMin(c.durata_min)}</span>
                      <span className="text-brand font-bold">{fmtEuro(c.costo_totale)}</span>
                      <span className="bg-slate-100 rounded-full px-2 py-0.5 text-[10px]">{c.sopralluoghi}x</span>
                    </div>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${colorForDurata(c.durata_min)}`}
                      style={{ width: `${Math.max(pct, 3)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
